import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface AppIconProps {
  title: string
  icon: LucideIcon
  color: 'emerald' | 'orange' | 'pink' | 'blue' | 'zinc' | 'indigo'
  to: string
  disabled?: boolean
  external?: boolean
  badge?: number
}

const colorClasses: Record<AppIconProps['color'], { bg: string; icon: string }> = {
  emerald: { bg: 'bg-emerald-500', icon: 'text-white' },
  orange: { bg: 'bg-orange-500', icon: 'text-white' },
  pink: { bg: 'bg-pink-500', icon: 'text-white' },
  blue: { bg: 'bg-blue-500', icon: 'text-white' },
  zinc: { bg: 'bg-zinc-500', icon: 'text-white' },
  indigo: { bg: 'bg-indigo-500', icon: 'text-white' },
}

export function AppIcon({ title, icon: Icon, color, to, disabled, external, badge }: AppIconProps) {
  const colors = colorClasses[color]

  const content = (
    <motion.div
      whileTap={disabled ? undefined : { scale: 0.95 }}
      className={cn(
        'relative flex flex-col items-center justify-center gap-2 p-4 rounded-2xl',
        'h-24 transition-opacity shadow-sm',
        colors.bg,
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      <Icon className={cn('h-8 w-8', colors.icon)} />
      <span className={cn('text-sm font-medium text-center leading-tight', colors.icon)}>
        {title}
      </span>
      {badge !== undefined && badge > 0 && (
        <span className="absolute -top-1 -right-1 min-w-5 h-5 flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold px-1.5">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </motion.div>
  )

  if (disabled) {
    return <div className="pointer-events-none">{content}</div>
  }

  if (external) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className="focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-2xl"
      >
        {content}
      </a>
    )
  }

  return (
    <Link to={to} className="focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-2xl">
      {content}
    </Link>
  )
}
